import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Camera, RefreshCw, ArrowRight, ShieldCheck, CheckCircle2, AlertCircle, UserCheck, MapPin, UserCog } from 'lucide-react';
import authService from '../services/authService';
import { useToast } from '../hooks/useToast';
import authStorage from '../utils/authStorage';
import { fetchStates, fetchCitiesForState } from '../services/locationService';

const UserProfile = () => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  // User details state
  const [userData, setUserData] = useState({
    id: '',
    name: '',
    emailId: '',
    mobileNo: '',
    photoUrl: '',
    signupAs: '',
    state: '',
    city: ''
  });

  // Name editing
  const [nameInput, setNameInput] = useState('');
  const [stateInput, setStateInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [savingName, setSavingName] = useState(false);

  // Email update OTP state
  const [emailStep, setEmailStep] = useState('IDLE'); // 'IDLE' | 'INPUT' | 'OTP'
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);
  const [emailTimer, setEmailTimer] = useState(0);
  const [emailError, setEmailError] = useState('');

  // Mobile update OTP state
  const [mobileStep, setMobileStep] = useState('IDLE'); // 'IDLE' | 'INPUT' | 'OTP'
  const [newMobile, setNewMobile] = useState('');
  const [mobileOtp, setMobileOtp] = useState(['', '', '', '', '', '']);
  const [sendingMobileOtp, setSendingMobileOtp] = useState(false);
  const [verifyingMobileOtp, setVerifyingMobileOtp] = useState(false);
  const [mobileTimer, setMobileTimer] = useState(0);
  const [mobileError, setMobileError] = useState('');


  // Avatar uploading
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    const loadStates = async () => {
      const list = await fetchStates();
      setStatesList(list);
    };
    loadStates();
  }, []);

  const handleStateSelectInProfile = async (selState) => {
    setStateInput(selState);
    setCityInput('');
    if (selState) {
      setLoadingCities(true);
      const cities = await fetchCitiesForState(selState);
      setCitiesList(cities);
      setLoadingCities(false);
    } else {
      setCitiesList([]);
    }
  };

  const handleSaveLocation = async (e) => {
    e.preventDefault();
    if (!stateInput) {
      toast.error('Please select a state.');
      return;
    }
    if (!cityInput) {
      toast.error('Please select a city.');
      return;
    }
    setSavingLocation(true);
    const res = await authService.updateProfileLocation(stateInput, cityInput);
    setSavingLocation(false);
    if (res.success) {
      toast.success('Location updated successfully!');
      loadProfile();
    } else {
      toast.error(res.error || 'Failed to update location');
    }
  };

  // Load user profile on mount
  const loadProfile = async () => {
    const storedUser = JSON.parse(authStorage.getItem('user') || '{}');
    const email = storedUser.emailId || storedUser.username;

    if (email && email.includes('@')) {
      const res = await authService.getUserProfile(email);
      if (res.success && res.data) {
        const u = res.data;
        const signupAsVal = u.signupAs || storedUser.signupAs || 'Vendor/Supplier';
        setUserData({
          id: u.id || '',
          name: u.name || '',
          emailId: u.emailId || '',
          mobileNo: u.mobileNo || '',
          photoUrl: u.photoUrl || '',
          signupAs: signupAsVal,
          state: u.state || '',
          city: u.city || ''
        });
        setNameInput(u.name || '');
        setStateInput(u.state || '');
        if (u.state) {
          fetchCitiesForState(u.state).then(c => {
            setCitiesList(c);
            setCityInput(u.city || '');
          });
        }
        authStorage.setItem('user', JSON.stringify({
          ...storedUser,
          id: u.id,
          name: u.name,
          emailId: u.emailId,
          mobileNo: u.mobileNo || '',
          photoUrl: u.photoUrl || '',
          signupAs: signupAsVal,
          state: u.state || '',
          city: u.city || ''
        }));
        return;
      }
    }

    setUserData({
      id: storedUser.id || '',
      name: storedUser.name || '',
      emailId: storedUser.emailId || storedUser.username || '',
      mobileNo: storedUser.mobileNo || '',
      photoUrl: storedUser.photoUrl || '',
      signupAs: storedUser.signupAs || 'Vendor/Supplier',
      state: storedUser.state || '',
      city: storedUser.city || ''
    });
    setNameInput(storedUser.name || '');
    setStateInput(storedUser.state || '');
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Email Timer countdown
  useEffect(() => {
    let interval = null;
    if (emailTimer > 0) {
      interval = setInterval(() => {
        setEmailTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [emailTimer]);

  // Mobile Timer countdown
  useEffect(() => {
    let interval = null;
    if (mobileTimer > 0) {
      interval = setInterval(() => {
        setMobileTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [mobileTimer]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Helper for image URLs
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // ── Save Name ──────────────────────────────────────────────────────────────
  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!nameInput.trim()) {
      toast.error('Please enter a valid name');
      return;
    }
    setSavingName(true);
    try {
      const res = await authService.updateProfileName(nameInput.trim());
      if (res.success && res.data) {
        toast.success('Name updated successfully!');
        setUserData((prev) => ({ ...prev, name: res.data.user?.name || nameInput.trim() }));
        window.dispatchEvent(new Event('profilePhotoChanged'));
      } else {
        toast.error(res.error || 'Failed to update name');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while updating name');
    } finally {
      setSavingName(false);
    }
  };

  // ── Profile Photo Upload ───────────────────────────────────────────────────
  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);
    setUploadingPhoto(true);

    try {
      const res = await authService.uploadProfilePhoto(formData);
      if (res.success && res.data) {
        toast.success('Profile picture updated successfully!');
        const newPhotoUrl = res.data.photoUrl || res.data.user?.photoUrl;
        setUserData((prev) => ({ ...prev, photoUrl: newPhotoUrl }));

        const storedUser = authStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userObj.photoUrl = newPhotoUrl;
          authStorage.setItem('user', JSON.stringify(userObj));
        }

        window.dispatchEvent(new Event('profilePhotoChanged'));
      } else {
        toast.error(res.error || 'Failed to upload image');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error uploading profile picture');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ── Email Update OTP ───────────────────────────────────────────────────────
  // ── Email Update OTP ───────────────────────────────────────────────────────
  const handleRequestEmailOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    const targetEmail = newEmail.trim().toLowerCase();
    if (!targetEmail || !targetEmail.includes('@')) {
      const msg = 'Please enter a valid email address';
      setEmailError(msg);
      toast.error(msg);
      return;
    }
    if (targetEmail === userData.emailId.toLowerCase()) {
      const msg = 'New email is identical to your current email';
      setEmailError(msg);
      toast.error(msg);
      return;
    }

    setSendingEmailOtp(true);
    try {
      const res = await authService.requestEmailOtp(targetEmail);
      if (res.success) {
        toast.success(`OTP code sent to ${targetEmail}`);
        setEmailStep('OTP');
        setEmailTimer(180);
        setEmailOtp(['', '', '', '', '', '']);
        setEmailError('');
      } else {
        const err = res.error || 'Failed to send OTP to email';
        setEmailError(err);
        toast.error(err);
      }
    } catch (err) {
      console.error(err);
      const msg = 'An error occurred while requesting email OTP';
      setEmailError(msg);
      toast.error(msg);
    } finally {
      setSendingEmailOtp(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    const code = emailOtp.join('');
    if (code.length < 6) {
      const msg = 'Please enter complete 6-digit OTP';
      setEmailError(msg);
      toast.error(msg);
      return;
    }

    setVerifyingEmailOtp(true);
    try {
      const res = await authService.verifyEmailOtp(newEmail.trim().toLowerCase(), code);
      if (res.success && res.data) {
        toast.success('Email address updated successfully!');
        const updatedEmail = res.data.user?.emailId || newEmail.trim().toLowerCase();
        setUserData((prev) => ({ ...prev, emailId: updatedEmail }));
        setEmailStep('IDLE');
        setNewEmail('');
        setEmailOtp(['', '', '', '', '', '']);
        setEmailError('');
        window.dispatchEvent(new Event('profilePhotoChanged'));
      } else {
        const err = res.error || 'Invalid OTP verification code';
        setEmailError(err);
        toast.error(err);
      }
    } catch (err) {
      console.error(err);
      const msg = 'An error occurred while verifying email OTP';
      setEmailError(msg);
      toast.error(msg);
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  const handleEmailOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...emailOtp];
    newOtp[index] = value.slice(-1);
    setEmailOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`email-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // ── Mobile Update OTP ──────────────────────────────────────────────────────
  const handleRequestMobileOtp = async (e) => {
    e.preventDefault();
    setMobileError('');
    const targetMobile = newMobile.trim();
    if (!/^\d{10}$/.test(targetMobile)) {
      const msg = 'Please enter a valid 10-digit mobile number';
      setMobileError(msg);
      toast.error(msg);
      return;
    }
    if (targetMobile === userData.mobileNo.trim()) {
      const msg = 'New mobile number is identical to your current mobile number';
      setMobileError(msg);
      toast.error(msg);
      return;
    }

    setSendingMobileOtp(true);
    try {
      const res = await authService.requestMobileOtp(targetMobile);
      if (res.success) {
        toast.success(`OTP code sent via SMS to ${targetMobile}`);
        setMobileStep('OTP');
        setMobileTimer(180);
        setMobileOtp(['', '', '', '', '', '']);
        setMobileError('');
      } else {
        const err = res.error || 'Failed to send SMS OTP';
        setMobileError(err);
        toast.error(err);
      }
    } catch (err) {
      console.error(err);
      const msg = 'An error occurred while requesting mobile OTP';
      setMobileError(msg);
      toast.error(msg);
    } finally {
      setSendingMobileOtp(false);
    }
  };

  const handleVerifyMobileOtp = async (e) => {
    e.preventDefault();
    setMobileError('');
    const code = mobileOtp.join('');
    if (code.length < 6) {
      const msg = 'Please enter complete 6-digit OTP';
      setMobileError(msg);
      toast.error(msg);
      return;
    }

    setVerifyingMobileOtp(true);
    try {
      const res = await authService.verifyMobileOtp(newMobile.trim(), code);
      if (res.success && res.data) {
        toast.success('Mobile number updated successfully!');
        const updatedMobile = res.data.user?.mobileNo || newMobile.trim();
        setUserData((prev) => ({ ...prev, mobileNo: updatedMobile }));
        setMobileStep('IDLE');
        setNewMobile('');
        setMobileOtp(['', '', '', '', '', '']);
        setMobileError('');
        window.dispatchEvent(new Event('profilePhotoChanged'));
      } else {
        const err = res.error || 'Invalid OTP verification code';
        setMobileError(err);
        toast.error(err);
      }
    } catch (err) {
      console.error(err);
      const msg = 'An error occurred while verifying mobile OTP';
      setMobileError(msg);
      toast.error(msg);
    } finally {
      setVerifyingMobileOtp(false);
    }
  };


  const handleMobileOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...mobileOtp];
    newOtp[index] = value.slice(-1);
    setMobileOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`mobile-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* ── Sleek Professional Header Card ───────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-5 md:p-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-5 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5 w-full sm:w-auto">
            {/* Avatar Circle (Mobile Only - Hidden on Desktop) */}
            <div className="relative flex-shrink-0 sm:hidden">
              <div className="w-20 h-20 sm:w-22 sm:h-22 rounded-2xl bg-[#e0f7f8] border-2 border-[#2ab391]/20 flex items-center justify-center overflow-hidden shadow-xs">
                {userData.photoUrl ? (
                  <img
                    src={getImageUrl(userData.photoUrl)}
                    alt="User Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#2ab391] to-[#1b8a70] text-white font-bold text-3xl flex items-center justify-center select-none shadow-inner">
                    {(userData.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-xl bg-[#2ab391] hover:bg-[#239b7d] text-white flex items-center justify-center shadow-md transition-all cursor-pointer"
                title="Upload Photo"
              >
                <Camera size={14} />
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoSelect}
                accept="image/*"
                className="hidden"
              />
            </div>

            {/* User Meta Summary */}
            <div className="space-y-2.5 w-full sm:w-auto text-center sm:text-left">
              {/* Line 1: User Name */}
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight block">
                {userData.name || 'User Profile'}
              </h1>

              {/* Line 2: Both Badges in Single Line */}
              <div className="flex items-center justify-center sm:justify-start gap-2 flex-nowrap overflow-x-auto">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap flex-shrink-0">
                  <ShieldCheck size={13} /> Active Account
                </span>
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap flex-shrink-0 ${
                  (userData.signupAs || 'Vendor/Supplier') === 'Architect/Designer'
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : (userData.signupAs || 'Vendor/Supplier') === 'User'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-teal-50 text-teal-700 border-teal-200'
                }`}>
                  <UserCheck size={13} /> {userData.signupAs || 'Vendor/Supplier'}
                </span>
              </div>

              {/* Line 3: Email, Phone, Location Pills */}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200/70 font-medium truncate max-w-full">
                  <Mail size={13} className="text-[#2ab391] flex-shrink-0" />
                  <span className="truncate">{userData.emailId || 'Not provided'}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200/70 font-medium whitespace-nowrap">
                  <Phone size={13} className="text-[#2ab391] flex-shrink-0" />
                  {userData.mobileNo ? `+91 ${userData.mobileNo}` : 'Not provided'}
                </span>
                <span className="inline-flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200/70 font-medium whitespace-nowrap">
                  <MapPin size={13} className="text-[#2ab391] flex-shrink-0" />
                  {userData.city && userData.state ? `${userData.city}, ${userData.state}` : userData.state || userData.city || 'Location Not Set'}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPhoto}
            className="inline-flex items-center justify-center gap-2 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl px-4 py-2 transition-all cursor-pointer shadow-2xs self-center sm:self-auto flex-shrink-0"
          >
            <Camera size={14} className="text-[#2ab391]" />
            {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
          </button>
        </div>
      </div>

      {/* ── Section 1: Personal Details ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
          <div className="w-9 h-9 rounded-xl bg-[#e0f7f8] text-[#2ab391] flex items-center justify-center">
            <UserCog size={18} />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Personal Information</h2>
            <p className="text-xs text-gray-500">Manage your public account name</p>
          </div>
        </div>

        <form onSubmit={handleSaveName} className="space-y-2">
          <label className="block text-xs font-semibold text-gray-700">
            Full Name <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Enter your full name"
              className="flex-1 border border-gray-300 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all shadow-2xs text-gray-800 h-10"
            />
            <button
              type="submit"
              disabled={savingName}
              className="border border-[#2ab391]/30 text-[#2ab391] hover:bg-[#e0f7f8] font-semibold px-5 h-10 rounded-xl text-xs transition-colors shadow-2xs flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 flex-shrink-0 w-full sm:w-auto"
            >
              {savingName ? 'Saving...' : 'Update Name'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Section 2: Location Information ────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
          <div className="w-9 h-9 rounded-xl bg-[#e0f7f8] text-[#2ab391] flex items-center justify-center">
            <MapPin size={18} />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Location Information</h2>
            <p className="text-xs text-gray-500">Update your registered state and city</p>
          </div>
        </div>

        <form onSubmit={handleSaveLocation} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                State <span className="text-red-500">*</span>
              </label>
              <select
                value={stateInput}
                onChange={(e) => handleStateSelectInProfile(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all shadow-2xs text-gray-800 h-10 cursor-pointer"
              >
                <option value="">Select State</option>
                {statesList.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <select
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                disabled={!stateInput || loadingCities}
                className={`w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all shadow-2xs text-gray-800 h-10 cursor-pointer ${!stateInput ? 'bg-gray-50 text-gray-400' : ''}`}
              >
                <option value="">{loadingCities ? 'Loading cities...' : !stateInput ? 'Select State First' : 'Select City'}</option>
                {citiesList.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingLocation}
              className="border border-[#2ab391]/30 text-[#2ab391] hover:bg-[#e0f7f8] font-semibold px-5 h-10 rounded-xl text-xs transition-colors shadow-2xs flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 w-full sm:w-auto"
            >
              {savingLocation ? 'Saving...' : 'Update Location'}
            </button>
          </div>
        </form>
      </div>


      {/* ── Section 2: Contact Details (Email & Mobile OTP Flow Cards) ─────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Email Address Card ────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-[#2ab391] flex items-center justify-center">
                  <Mail size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Email Address</h2>
                  <p className="text-xs text-gray-500">Requires OTP sent to new email</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                <CheckCircle2 size={12} /> Verified
              </span>
            </div>

            {/* Current Email Display */}
            <div className="bg-gray-50/80 rounded-xl p-4 mb-5 border border-gray-100">
              <span className="text-[11px] font-medium text-gray-400 block uppercase tracking-wider mb-1">
                Current Registered Email
              </span>
              <span className="text-sm font-semibold text-gray-800 break-all">
                {userData.emailId}
              </span>
            </div>

            {/* Step 1: Input New Email Form */}
            {emailStep === 'INPUT' && (
              <form onSubmit={handleRequestEmailOtp} className="space-y-4 border-t border-gray-100 pt-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    New Email Address
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      setEmailError('');
                    }}
                    placeholder="name@example.com"
                    required
                    className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all shadow-2xs text-gray-800"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    We will send a 6-digit verification code to this email address.
                  </p>

                  {emailError && (
                    <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium mt-2 bg-red-50 border border-red-200 p-2.5 rounded-lg">
                      <AlertCircle size={14} className="flex-shrink-0" />
                      <span>{emailError}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEmailStep('IDLE');
                      setEmailError('');
                    }}
                    className="text-gray-600 hover:bg-gray-100 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingEmailOtp}
                    className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-4 py-2 rounded-xl text-xs transition-all shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {sendingEmailOtp ? 'Sending OTP...' : 'Send OTP'}
                    <ArrowRight size={13} />
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Verification Code Entry */}
            {emailStep === 'OTP' && (
              <form onSubmit={handleVerifyEmailOtp} className="space-y-5 border-t border-gray-100 pt-4">
                <div className="bg-[#e0f7f8]/60 p-3.5 rounded-xl border border-[#2ab391]/30 text-center">
                  <p className="text-xs font-medium text-gray-700">
                    OTP sent to <span className="font-bold text-gray-900">{newEmail}</span>
                  </p>
                  {emailTimer > 0 ? (
                    <p className="text-xs text-[#2ab391] font-semibold mt-0.5">
                      Expires in {formatTimer(emailTimer)}
                    </p>
                  ) : (
                    <p className="text-xs text-red-500 font-semibold mt-0.5">OTP Expired</p>
                  )}
                </div>

                <div className="flex items-center justify-center gap-1.5 sm:gap-2.5 py-1 px-1 overflow-x-auto max-w-full">
                  {emailOtp.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`email-otp-${idx}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => {
                        handleEmailOtpChange(idx, e.target.value);
                        setEmailError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !digit && idx > 0) {
                          const prevInput = document.getElementById(`email-otp-${idx - 1}`);
                          if (prevInput) prevInput.focus();
                        }
                      }}
                      className="w-8 sm:w-10 h-10 sm:h-12 border border-gray-300 rounded-xl text-center font-bold text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2ab391] focus:border-[#2ab391] bg-white shadow-2xs transition-all flex-shrink-0"
                    />
                  ))}
                </div>

                {emailError && (
                  <div className="flex items-center justify-center gap-1.5 text-xs text-red-600 font-medium bg-red-50 border border-red-200 p-2.5 rounded-lg">
                    <AlertCircle size={14} className="flex-shrink-0" />
                    <span>{emailError}</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleRequestEmailOtp}
                    disabled={sendingEmailOtp || emailTimer > 150}
                    className="text-[11px] font-semibold text-[#2ab391] hover:underline disabled:opacity-40 flex items-center gap-1 cursor-pointer self-start sm:self-center"
                  >
                    <RefreshCw size={11} /> Resend OTP
                  </button>

                  <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setEmailStep('INPUT');
                        setEmailError('');
                      }}
                      className="text-gray-500 hover:bg-gray-100 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer flex-1 sm:flex-initial text-center"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={verifyingEmailOtp || emailOtp.join('').length < 6}
                      className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-4 py-2 rounded-xl text-xs transition-all shadow-xs cursor-pointer disabled:opacity-50 flex-1 sm:flex-initial text-center"
                    >
                      {verifyingEmailOtp ? 'Verifying...' : 'Verify & Update Email'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {emailStep === 'IDLE' && (
            <button
              type="button"
              onClick={() => {
                setEmailStep('INPUT');
                setNewEmail('');
                setEmailError('');
              }}
              className="w-full mt-2 border border-[#2ab391]/30 text-[#2ab391] hover:bg-[#e0f7f8] font-semibold py-2.5 px-4 rounded-xl text-xs transition-colors text-center cursor-pointer"
            >
              Update Email Address
            </button>
          )}
        </div>

        {/* ── Mobile Number Card ────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-[#2ab391] flex items-center justify-center">
                  <Phone size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Mobile Number</h2>
                  <p className="text-xs text-gray-500">Requires SMS OTP sent to new mobile</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                <CheckCircle2 size={12} /> Verified
              </span>
            </div>

            {/* Current Mobile Display */}
            <div className="bg-gray-50/80 rounded-xl p-4 mb-5 border border-gray-100">
              <span className="text-[11px] font-medium text-gray-400 block uppercase tracking-wider mb-1">
                Current Registered Mobile
              </span>
              <span className="text-sm font-semibold text-gray-800">
                {userData.mobileNo ? `+91 ${userData.mobileNo}` : 'Not registered'}
              </span>
            </div>

            {/* Step 1: Input New Mobile Form */}
            {mobileStep === 'INPUT' && (
              <form onSubmit={handleRequestMobileOtp} className="space-y-4 border-t border-gray-100 pt-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    New Mobile Number (10 Digits)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-semibold">
                      +91
                    </span>
                    <input
                      type="tel"
                      maxLength={10}
                      value={newMobile}
                      onChange={(e) => {
                        setNewMobile(e.target.value.replace(/\D/g, ''));
                        setMobileError('');
                      }}
                      placeholder="9876543210"
                      required
                      className="w-full border border-gray-300 rounded-xl pl-11 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all shadow-2xs text-gray-800"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    We will send a 6-digit SMS OTP code to this mobile number.
                  </p>

                  {mobileError && (
                    <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium mt-2 bg-red-50 border border-red-200 p-2.5 rounded-lg">
                      <AlertCircle size={14} className="flex-shrink-0" />
                      <span>{mobileError}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileStep('IDLE');
                      setMobileError('');
                    }}
                    className="text-gray-600 hover:bg-gray-100 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingMobileOtp || newMobile.length !== 10}
                    className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-4 py-2 rounded-xl text-xs transition-all shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {sendingMobileOtp ? 'Sending SMS...' : 'Send SMS OTP'}
                    <ArrowRight size={13} />
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Verification Code Entry */}
            {mobileStep === 'OTP' && (
              <form onSubmit={handleVerifyMobileOtp} className="space-y-5 border-t border-gray-100 pt-4">
                <div className="bg-[#e0f7f8]/60 p-3.5 rounded-xl border border-[#2ab391]/30 text-center">
                  <p className="text-xs font-medium text-gray-700">
                    SMS OTP sent to <span className="font-bold text-gray-900">+91 {newMobile}</span>
                  </p>
                  {mobileTimer > 0 ? (
                    <p className="text-xs text-[#2ab391] font-semibold mt-0.5">
                      Expires in {formatTimer(mobileTimer)}
                    </p>
                  ) : (
                    <p className="text-xs text-red-500 font-semibold mt-0.5">OTP Expired</p>
                  )}
                </div>

                <div className="flex items-center justify-center gap-1.5 sm:gap-2.5 py-1 px-1 overflow-x-auto max-w-full">
                  {mobileOtp.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`mobile-otp-${idx}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => {
                        handleMobileOtpChange(idx, e.target.value);
                        setMobileError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !digit && idx > 0) {
                          const prevInput = document.getElementById(`mobile-otp-${idx - 1}`);
                          if (prevInput) prevInput.focus();
                        }
                      }}
                      className="w-8 sm:w-10 h-10 sm:h-12 border border-gray-300 rounded-xl text-center font-bold text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2ab391] focus:border-[#2ab391] bg-white shadow-2xs transition-all flex-shrink-0"
                    />
                  ))}
                </div>

                {mobileError && (
                  <div className="flex items-center justify-center gap-1.5 text-xs text-red-600 font-medium bg-red-50 border border-red-200 p-2.5 rounded-lg">
                    <AlertCircle size={14} className="flex-shrink-0" />
                    <span>{mobileError}</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleRequestMobileOtp}
                    disabled={sendingMobileOtp || mobileTimer > 150}
                    className="text-[11px] font-semibold text-[#2ab391] hover:underline disabled:opacity-40 flex items-center gap-1 cursor-pointer self-start sm:self-center"
                  >
                    <RefreshCw size={11} /> Resend SMS OTP
                  </button>

                  <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileStep('INPUT');
                        setMobileError('');
                      }}
                      className="text-gray-500 hover:bg-gray-100 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer flex-1 sm:flex-initial text-center"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={verifyingMobileOtp || mobileOtp.join('').length < 6}
                      className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-4 py-2 rounded-xl text-xs transition-all shadow-xs cursor-pointer disabled:opacity-50 flex-1 sm:flex-initial text-center"
                    >
                      {verifyingMobileOtp ? 'Verifying...' : 'Verify & Update Mobile'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {mobileStep === 'IDLE' && (
            <button
              type="button"
              onClick={() => {
                setMobileStep('INPUT');
                setNewMobile('');
                setMobileError('');
              }}
              className="w-full mt-2 border border-[#2ab391]/30 text-[#2ab391] hover:bg-[#e0f7f8] font-semibold py-2.5 px-4 rounded-xl text-xs transition-colors text-center cursor-pointer"
            >
              Update Mobile Number
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default UserProfile;

